import { useMutation, useQueryClient, MutationKey } from "react-query";
import Axios from "axios";
import { EnqueueSnackbar, useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { RemoteApiError } from "../useRemote/types";

export type CheckInFleetBookingPayload = {
  bookingId: number;
  odometer?: number | null;
  conditionNotes?: string | null;
  photos?: File[];
};

/**
 * Check-in a fleet booking (start trip).
 * POST /fleet/bookings/:id/check-in
 * Multipart: odometer, conditionNotes, photos.
 */
export function useRemoteCheckInFleetBooking() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation<void, RemoteApiError, CheckInFleetBookingPayload>(
    ["fleet", "check-in-booking"] as MutationKey,
    async ({ bookingId, odometer, conditionNotes, photos }: CheckInFleetBookingPayload) => {
      const formData = new FormData();
      if (odometer != null) formData.append("odometer", String(odometer));
      if (conditionNotes != null && conditionNotes !== "")
        formData.append("conditionNotes", conditionNotes);
      if (photos?.length) {
        photos.forEach(file => formData.append("photos", file, file.name));
      }
      await Axios.post(`/fleet/bookings/${bookingId}/check-in`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["fleet", "vehicles"]);
        enqueueSnackbar(t("fleet_checkInCompleted"), { variant: "success" });
      },
      onError: (error: RemoteApiError) => {
        handleFleetBookingError(error, enqueueSnackbar, t);
      }
    }
  );
}

function handleFleetBookingError(
  error: RemoteApiError,
  enqueueSnackbar: EnqueueSnackbar,
  t: TFunction
) {
  const backendMessage = error.response?.data?.message ?? error.message;

  if (backendMessage.includes("You can only check in approved bookings")) {
    enqueueSnackbar(t("_fleetBookingEventCannotCheckInWithoutApproval"), { variant: "error" });
  } else if (backendMessage.includes("Vehicle already checked out for this booking")) {
    enqueueSnackbar(t("_fleetBookingEventAlreadyCheckedOut"), { variant: "error" });
  } else {
    enqueueSnackbar(t("There was a server error"), { variant: "error" });
  }
}
